import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import styled from 'styled-components';

import Header from 'common/Header';
import { useAppSelector } from 'store/hook';
import { RecommendationCommunityOverviewType } from 'types/recommendations';

import { OVERALL_VIEW_ID, RECOMMENDATION_OVERVIEW_FAILED } from './constants';
import RecommendationBreadcrumb from './RecommendationBreadcrumb';
import RecommendationCommunityList from './RecommendationCommunityList';
import RecommendationCompany from './RecommendationCompany';
import useRecommendationDetailsContext from './RecommendationDetailsContext';
import RecommendationDetailsSidebar from './RecommendationDetailsSidebar';
import useRecommendationsContext from './RecommendationsContext';

const Container = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  width: 100%;
  overflow-y: auto;
`;

const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  margin-right: 10px;
  padding: 1px;
  background-image:
    linear-gradient(var(--white), var(--white)),
    linear-gradient(to bottom, var(--competitor-purple), var(--negative-orange) 50%, var(--flamingo-pink));
  background-origin: border-box;
  background-clip: content-box, border-box;
`;

const Layout = styled.div`
  display: flex;
  flex-direction: row;
  border-top: 1px solid var(--line-gray);
  height: calc(100dvh - 130px);
`;

const Content = styled.div`
  border-left: 1px solid var(--line-gray);
  padding: 30px 47px;
  flex: 1 1 auto;
  min-width: 0;
  overflow-y: auto;

  &.communityView {
    padding: 30px 216px;
  }
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px 30px;
`;

const RecommendationDetails = () => {
  const { company } = useRecommendationsContext();
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const { recommendation, filters, selectedItem } = useRecommendationDetailsContext();
  const [communityOverviews, setCommunityOverviews] = useState<RecommendationCommunityOverviewType[]>();

  const failedOverviews =
    communityOverviews?.filter((overview) => overview.status === RECOMMENDATION_OVERVIEW_FAILED) || [];

  const showProblemSolution = !selectedCommunity && selectedItem === OVERALL_VIEW_ID;

  useEffect(() => {
    if (!recommendation) {
      return;
    }

    const communityIds = selectedCommunity ? [selectedCommunity.id] : filters.communities;

    if (communityIds.length !== 0) {
      setCommunityOverviews(recommendation.community_overviews.filter((co) => communityIds.includes(co.community_id)));
    } else {
      setCommunityOverviews(recommendation.community_overviews);
    }
  }, [recommendation, filters, selectedCommunity]);

  return (
    <Container>
      <Header
        title={recommendation.title}
        breadcrumbs={<RecommendationBreadcrumb company={company.name} />}
        iconComponent={
          <IconContainer>
            <img src="/icons/ai-blob.svg" alt="ai-blob" height="30px" width="30px" />
          </IconContainer>
        }
      />
      <Layout>
        {!selectedCommunity && <RecommendationDetailsSidebar />}
        <Content
          className={classNames('mini-scroll', {
            communityView: selectedCommunity,
          })}
        >
          {showProblemSolution && <RecommendationCompany recommendation={recommendation} />}
          {showProblemSolution && failedOverviews.length > 0 && (
            <Main>
              <RecommendationCommunityList data={failedOverviews} />
            </Main>
          )}
          {!showProblemSolution && (
            <Main>
              <RecommendationCommunityList data={communityOverviews} />
            </Main>
          )}
        </Content>
      </Layout>
    </Container>
  );
};

export default RecommendationDetails;
